<template>
  <v-card
    outlined
    :style="isSelected ? `border: 3px solid #1867c0 !important` : ''"
    @click="$emit('select')"
    style="cursor: pointer"
  >
    <v-card-title class="py-2">
      <div class="d-flex align-center flex-grow-1">
        <v-icon small class="mr-2">mdi-numeric-{{ index + 1 }}-circle</v-icon>
        <div class="flex-grow-1">
          <div class="subtitle-2">{{ scenario.strategy }}</div>
          <div v-if="scenario.strategyInfo" class="caption text--secondary">
            {{ scenario.strategyInfo.title }}
          </div>
        </div>
      </div>
    </v-card-title>

    <v-divider class="mb-2"></v-divider>

    <v-card-text class="pt-0 pb-1">
      <!-- Summary Statistics -->
      <div class="text-caption mb-2 d-flex align-center justify-space-between">
        <span>
          <strong>{{ scenario.assignedUnits.length }}</strong> Einheiten,
          <strong>{{ scenario.totalAssignedPersonnel }}</strong> Personen
        </span>
        <v-chip
          v-if="remainingPersonnel > 0"
          x-small
          color="blue-grey"
          text-color="white"
          class="ml-2"
        >
          {{ remainingPersonnel }} P. übrig
        </v-chip>
      </div>

      <!-- Unit Status Overview -->
      <div class="scenario-summary">
        <div
          v-for="(assignment, unitIndex) in scenario.assignedUnits"
          :key="unitIndex"
          class="text-caption mb-1 d-flex align-center"
        >
          <v-icon x-small class="mr-1" :color="getUnitStatusColor(assignment)">
            {{ getUnitStatusIcon(assignment) }}
          </v-icon>
          <span :class="getUnitStatusTextColor(assignment)" class="flex-grow-1">
            <strong>{{
              assignment.unit.shortTitle || assignment.unit.title
            }}</strong>
            -
            {{ assignment.crewCombination.title }}
            <span v-if="getUnitMissingText(assignment)" class="red--text ml-1">
              ({{ getUnitMissingText(assignment) }})
            </span>
          </span>
        </div>
      </div>
    </v-card-text>

    <v-divider></v-divider>
    <!-- Personnel Status Summary -->
    <v-card-text class="py-1">
      <div class="d-flex align-center justify-space-between text-caption mb-1">
        <div class="d-flex align-center">
          <v-icon x-small color="green" class="mr-1">mdi-account-check</v-icon>
          <span class="green--text"
            >{{ assignedPersonnelCount }} zugewiesen</span
          >
        </div>
        <div class="d-flex align-center">
          <v-icon x-small color="red" class="mr-1">mdi-account-minus</v-icon>
          <span class="red--text">{{ missingPersonnelCount }} fehlen</span>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "ScenarioCard",
  props: {
    scenario: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    availablePersonnel: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    remainingPersonnel() {
      // Calculate how many personnel are left unassigned in this scenario
      if (
        !this.availablePersonnel.length ||
        !this.scenario.totalAssignedPersonnel
      ) {
        return 0;
      }

      const totalAvailable = this.availablePersonnel.filter(
        (person) => person.status && person.status.id === "1"
      ).length;

      return Math.max(0, totalAvailable - this.scenario.totalAssignedPersonnel);
    },

    assignedPersonnelCount() {
      let totalAssigned = 0;
      this.scenario.assignedUnits.forEach((assignment) => {
        const fulfillmentStatus = this.getFulfillmentStatusObject(assignment);
        if (fulfillmentStatus && fulfillmentStatus.totalAvailable) {
          totalAssigned += fulfillmentStatus.totalAvailable;
        }
      });
      return totalAssigned;
    },

    missingPersonnelCount() {
      let totalMissing = 0;
      this.scenario.assignedUnits.forEach((assignment) => {
        const fulfillmentStatus = this.getFulfillmentStatusObject(assignment);
        if (
          fulfillmentStatus &&
          fulfillmentStatus.totalRequired &&
          fulfillmentStatus.totalAvailable
        ) {
          const missing = Math.max(
            0,
            fulfillmentStatus.totalRequired - fulfillmentStatus.totalAvailable
          );
          totalMissing += missing;
        }
      });
      return totalMissing;
    },

    totalRequiredPersonnelCount() {
      let totalRequired = 0;
      this.scenario.assignedUnits.forEach((assignment) => {
        const fulfillmentStatus = this.getFulfillmentStatusObject(assignment);
        if (fulfillmentStatus && fulfillmentStatus.totalRequired) {
          totalRequired += fulfillmentStatus.totalRequired;
        }
      });
      return totalRequired;
    },
  },
  methods: {
    getFulfillmentStatusObject(assignment) {
      // Get the full fulfillment status object
      if (assignment.fulfillmentStatus) {
        return assignment.fulfillmentStatus;
      } else if (
        assignment.fulfillmentResult &&
        assignment.fulfillmentResult.fulfillmentStatus
      ) {
        return assignment.fulfillmentResult.fulfillmentStatus;
      }
      return null;
    },

    getUnitFulfillmentStatus(assignment) {
      // Handle both fulfillmentStatus and fulfillmentResult structures
      const fulfillmentStatus = this.getFulfillmentStatusObject(assignment);
      return fulfillmentStatus?.status || "not_fulfilled";
    },

    getUnitStatusColor(assignment) {
      const status = this.getUnitFulfillmentStatus(assignment);
      switch (status) {
        case "fulfilled":
          return "green";
        case "partially_fulfilled":
          return "orange";
        case "not_fulfilled":
          return "red";
        default:
          return "grey";
      }
    },

    getUnitStatusTextColor(assignment) {
      const status = this.getUnitFulfillmentStatus(assignment);
      switch (status) {
        case "fulfilled":
          return "green--text";
        case "partially_fulfilled":
          return "orange--text";
        case "not_fulfilled":
          return "red--text";
        default:
          return "grey--text";
      }
    },

    getUnitStatusIcon(assignment) {
      const status = this.getUnitFulfillmentStatus(assignment);
      switch (status) {
        case "fulfilled":
          return "mdi-check-circle";
        case "partially_fulfilled":
          return "mdi-alert-circle";
        case "not_fulfilled":
          return "mdi-close-circle";
        default:
          return "mdi-help-circle";
      }
    },

    getUnitMissingText(assignment) {
      const fulfillmentStatus = this.getFulfillmentStatusObject(assignment);
      const missing = [];

      if (fulfillmentStatus && fulfillmentStatus.positionDetails) {
        fulfillmentStatus.positionDetails.forEach((position) => {
          if (position.missing > 0 && !position.isOptional) {
            const positionName =
              position.positionData.position.shortTitle || position.position;
            missing.push(`${position.missing}x ${positionName}`);
          }
        });
      }

      return missing.length > 0 ? missing.join(", ") : "";
    },
  },
};
</script>

<style scoped>
.scenario-summary {
  max-height: 120px;
  overflow-y: auto;
}

.scenario-summary::-webkit-scrollbar {
  width: 4px;
}

.scenario-summary::-webkit-scrollbar-track {
  background: transparent;
}

.scenario-summary::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.scenario-summary::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}
</style>
